/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useState } from 'react';
import { useFetcher } from 'react-router';
import { resourceRoutes } from '@/route-paths';
import type { ShopperSearch } from '@/scapi';
import type { loader as categoryProductsLoader } from '@/routes/resource.category-products';

export interface UseCategoryLoadMoreParams {
    categoryId: string;
    /** Offset immediately after the products already rendered on the initial page load */
    initialOffset: number;
    total: number;
    refine: string[];
    sort?: string;
}

/**
 * Accumulates additional pages of category search results on top of the initial
 * SSR-rendered page, driving the PLP's "See More Products" button. Fetches via a
 * dedicated resource route (`resource.category-products`) rather than the category
 * route's own loader, since that loader's critical/non-critical streaming split and
 * Page Designer data aren't needed for subsequent pages.
 */
export function useCategoryLoadMore({ categoryId, initialOffset, total, refine, sort }: UseCategoryLoadMoreParams) {
    const fetcher = useFetcher<typeof categoryProductsLoader>();
    const [hits, setHits] = useState<ShopperSearch.schemas['ProductSearchHit'][]>([]);
    const [offset, setOffset] = useState(initialOffset);

    const isLoading = fetcher.state !== 'idle';
    const hasMore = offset < total;

    const loadMore = useCallback(() => {
        if (isLoading || !hasMore) return;

        const params = new URLSearchParams();
        params.set('categoryId', categoryId);
        params.set('offset', String(offset));
        if (sort) params.set('sort', sort);
        refine.forEach((r) => params.append('refine', r));

        void fetcher.load(`${resourceRoutes.categoryProducts}?${params.toString()}`);
        // fetcher is stable across renders; omitting it avoids re-creating this callback on every fetcher.state change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, offset, sort, refine, isLoading, hasMore]);

    useEffect(() => {
        const newHits = fetcher.data?.success ? (fetcher.data.result?.hits ?? []) : [];
        if (newHits.length > 0) {
            setHits((prev) => [...prev, ...newHits]);
            setOffset((prev) => prev + newHits.length);
        }
    }, [fetcher.data]);

    return { hits, loadMore, isLoading, hasMore };
}
